import { MouseEvent, ReactNode } from 'react';
import { alpha, Box, IconButton, TableCell, useTheme } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import NextLink from 'next/link';
import CellValue from './CellValue';

export type OpenInPreference = 'fullPage' | 'sidePanel';

export type OpenRecordConfig<T> = {
  columnName: string;
  getLabel: (item: T) => ReactNode;
  getHref: (item: T) => string;
  onPreview?: (item: T) => void;
};

const ARROW_WIDTH_PX = 28;

const RecordOpenCell = ({
  label,
  href,
  onPreview,
  cellSx,
  columnWidthPx,
  openIn = 'sidePanel',
}: {
  label: ReactNode;
  href: string;
  onPreview?: () => void;
  cellSx: object;
  columnWidthPx?: number;
  openIn?: OpenInPreference;
}) => {
  const theme = useTheme();
  const hoverBorderColor = alpha(theme.palette.text.primary, 0.24);
  const openInSidePanel = openIn !== 'fullPage' && Boolean(onPreview);

  const open = (event: MouseEvent<HTMLElement>) => {
    if (!openInSidePanel || !onPreview) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    onPreview();
  };

  return (
    <TableCell
      sx={{
        ...cellSx,
        position: (cellSx as { position?: string }).position ?? 'relative',
        outline: 'none',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: '1px',
          border: `1px solid ${hoverBorderColor}`,
          borderRadius: '4px',
          pointerEvents: 'none',
          opacity: 0,
        },
        '&:hover::after': {
          opacity: 1,
        },
        '&:hover .record-open-arrow': {
          opacity: 1,
        },
        'body[data-inline-editing-active="true"] &::after, body[data-inline-editing-active="true"] & .record-open-arrow':
          {
            opacity: 0,
          },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: '28px',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Box
          component={NextLink}
          href={href}
          onClick={open}
          sx={{
            flex: 1,
            minWidth: 0,
            color: 'text.primary',
            textDecoration: 'none',
          }}
        >
          <CellValue
            cell={{ value: label }}
            columnWidthPx={columnWidthPx != null ? columnWidthPx - ARROW_WIDTH_PX : undefined}
          />
        </Box>
        <Box
          className="record-open-arrow"
          sx={{
            flexShrink: 0,
            opacity: 0,
            transition: 'opacity 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            component={NextLink}
            href={href}
            aria-label="Open"
            size="small"
            onClick={open}
            sx={{
              width: 24,
              height: 24,
              p: 0,
              border: `1px solid ${hoverBorderColor}`,
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ArrowOutwardIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </TableCell>
  );
};

export default RecordOpenCell;
